import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";

const DialogTags2 = ({ open, handleClose, onSave, selectedTags: initialSelectedTags }) => {
  const [categories, setCategories] = useState([]); // Tutti i tag disponibili
  const [selectedTags, setSelectedTags] = useState(initialSelectedTags || []); // Tag selezionati
  const [groupedCategories, setGroupedCategories] = useState({}); // Tag raggruppati per lettera
  const [searchQuery, setSearchQuery] = useState(""); // Stato per la ricerca
  const [expandedSections, setExpandedSections] = useState([]); // Stato per tenere traccia degli accordion aperti

  useEffect(() => {
    if (initialSelectedTags) {
      setSelectedTags(initialSelectedTags);
    }
  }, [initialSelectedTags]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/taxonomies?type=tag`);
        const tags = response.data.data || [];
        setCategories(tags);
        setGroupedCategories(groupCategoriesAlphabetically(tags));
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Funzione per raggruppare i tag alfabeticamente
  const groupCategoriesAlphabetically = (tags) => {
    return tags.reduce((acc, tag) => {
      const letter = tag.name[0].toUpperCase();
      if (!acc[letter]) {
        acc[letter] = [];
      }
      acc[letter].push(tag);
      return acc;
    }, {});
  };

  // Filtra i tag in base alla ricerca
  const filteredTags = categories.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Raggruppa i tag filtrati alfabeticamente
  const groupedFilteredCategories = groupCategoriesAlphabetically(filteredTags);

  // Apri automaticamente gli accordion che contengono risultati corrispondenti alla ricerca
  useEffect(() => {
    const matchingLetters = Object.keys(groupedFilteredCategories);
    setExpandedSections(matchingLetters);
  }, [searchQuery]);

  // Funzione per gestire l'espansione degli accordion manualmente
  const handleAccordionToggle = (letter) => {
    setExpandedSections((prev) =>
      prev.includes(letter) ? prev.filter((l) => l !== letter) : [...prev, letter]
    );
  };

  // Aggiunge un tag selezionato
  const handleTagSelect = (tag) => {
    if (!selectedTags.some((t) => t.id === tag.id)) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  // Rimuove un tag selezionato
  const handleTagRemove = (tagId) => {
    setSelectedTags(selectedTags.filter((tag) => tag.id !== tagId));
  };

  // Salvataggio
  const handleSave = () => {
    onSave(selectedTags);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: "bold",
          backgroundColor: "#191919",
          color: "white",
          fontFamily: "Urbanist",
        }}
      >
        Associated Tags
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#191919", color: "white" }}>
        {/* Campo di ricerca */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            marginBottom: "16px",
            backgroundColor: "#fff",
            borderRadius: "5px",
            input: { color: "#000" },
          }}
        />

        {/* Tag già associati */}
        <div className="flex flex-wrap gap-2 mb-4">
          {selectedTags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              onDelete={() => handleTagRemove(tag.id)}
              deleteIcon={
                <span
                  style={{
                    color: "rgb(var(--global-color-primary))",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  &times;
                </span>
              }
              sx={{
                backgroundColor: "transparent",
                border: "1.5px solid rgb(var(--global-color-primary))",
                borderRadius: "5px",
                fontFamily: "Urbanist",
                color: "rgb(var(--global-color-primary))",
                fontWeight: "bold",
              }}
            />
          ))}
        </div>

        {/* Accordion per i tag disponibili */}
        {Object.keys(groupedFilteredCategories)
          .sort()
          .map((letter) => (
            <Accordion
              key={letter}
              expanded={expandedSections.includes(letter)}
              onChange={() => handleAccordionToggle(letter)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                sx={{
                  backgroundColor: "#5a5b5b",
                  color: "#fff",
                }}
              >
                <Typography sx={{ fontWeight: "bold", fontFamily: "Urbanist" }}>
                  {letter}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: "#191919",
                  boxShadow: "none",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {groupedFilteredCategories[letter].map((category) => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    onClick={() => handleTagSelect(category)}
                    disabled={selectedTags.some((t) => t.id === category.id)}
                    sx={{
                      backgroundColor: selectedTags.some((t) => t.id === category.id)
                        ? "rgba(255,255,255,0.3)"
                        : "white",
                      fontFamily: "Urbanist",
                      color: selectedTags.some((t) => t.id === category.id)
                        ? "gray"
                        : "black",
                      fontWeight: "bold",
                      cursor: selectedTags.some((t) => t.id === category.id)
                        ? "not-allowed"
                        : "pointer",
                      "&:hover": {
                        backgroundColor: selectedTags.some((t) => t.id === category.id)
                          ? "rgba(255,255,255,0.3)"
                          : "rgb(var(--global-color-primary))",
                        color: "black",
                      },
                    }}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#191919" }}>
        <Button
          onClick={handleClose}
          sx={{
            backgroundColor: "gray",
            color: "white",
            "&:hover": { backgroundColor: "rgba(128, 128, 128, 0.8)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          sx={{
            backgroundColor: "rgb(var(--global-color-primary))",
            color: "black",
            fontWeight: "bold",
            "&:hover": { backgroundColor: "rgba(var(--global-color-primary), 0.8)" },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogTags2;
